import { type ButtonHTMLAttributes, forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { interactionSoundProps } from "../interaction-sound";
import { cn } from "../lib/cn";

/**
 * Chip. Inline pill primitive distinct from Badge:
 *   - Badge is the "small status label" tag (mono caps, tightly padded).
 *   - Chip is the "soft inline pill" tag (sans, looser padding, dashed
 *     variant for "paused"/"deferred" states).
 *
 * Used by /plan paused-items chips, library theme chips, reflection
 * chips, persona chips, and anywhere "tap this to filter / re-anchor"
 * lives. Two tones: `soft` (cream-fill, mist-dark border) and `paused`
 * (transparent fill, dashed mist-dark border).
 */
export type ChipTone = "soft" | "paused" | "active" | "ghost";

const TONE: Record<ChipTone, string> = {
  soft: "border-border-strong bg-cream text-ink-body",
  paused: "border-dashed border-border-strong bg-transparent text-ink-tertiary",
  active: "border-teal-600 bg-surface-secondary text-teal-800",
  ghost: "border-transparent bg-transparent text-ink-secondary",
};

export interface ChipBaseProps {
  tone?: ChipTone;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  className?: string;
  children?: ReactNode;
}

/** Static (non-interactive) chip - renders as <span>. */
export type ChipProps = ChipBaseProps & HTMLAttributes<HTMLSpanElement>;

export function Chip({
  tone = "soft",
  leadingIcon,
  trailingIcon,
  className,
  children,
  ...rest
}: ChipProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium",
        TONE[tone],
        className,
      )}
      {...rest}
    >
      {leadingIcon}
      {children}
      {trailingIcon}
    </span>
  );
}

/** Interactive chip - renders as <button>. */
export type ChipButtonProps = ChipBaseProps & ButtonHTMLAttributes<HTMLButtonElement>;

export const ChipButton = forwardRef<HTMLButtonElement, ChipButtonProps>(function ChipButton(
  { tone = "soft", leadingIcon, trailingIcon, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled}
      {...interactionSoundProps(disabled ? "none" : "action")}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium transition-colors duration-200 ease-[var(--ease-out-expo)] hover:border-border-primary hover:bg-surface-secondary hover:text-teal-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600",
        TONE[tone],
        className,
      )}
      {...rest}
    >
      {leadingIcon}
      {children}
      {trailingIcon}
    </button>
  );
});
